import { onLanguageChanged } from '@thiememeulenhoff/translations';
import { LitElement } from 'lit';
import { property } from 'lit/decorators.js';
import {
  UserActivityEvent,
  UserActivityEventType,
} from '@thiememeulenhoff/types';
import { dispatchCustomEvent } from './event.helper';

type DispatchCustomEvent = {
  eventName: string;
  element?: LitElement | Document | HTMLElement;
  detail?: CustomEvent['detail'];
  bubbles?: CustomEvent['bubbles'];
  cancelable?: CustomEvent['cancelable'];
};

type AllowedElements = Element | HTMLElement | Document | Window;

export interface EventListener {
  name: string;
  callback: EventListenerOrEventListenerObject;
  element: AllowedElements;
}

export class TmElement extends LitElement {
  /**
   * Property to set the `data-test` id attribute to the host element.
   * It is auto filled with the element's tag name.
   */
  @property({
    attribute: 'data-test',
    reflect: true,
  })
  protected testId: string = this.tagName.toLowerCase();

  protected onCreate?: () => void;

  protected onDestroy?: () => void;

  #listeners: Array<EventListener> = [];

  /**
   * Add event listeners that will automatically be removed on component destroy.
   * Use last parameter to specify the context (defaults to `this`).
   * When called with a known built-in event name (e.g. `'click'`, `'dragleave'`),
   * the callback is automatically typed with the corresponding event type (e.g. `MouseEvent`, `DragEvent`).
   * When called with a custom event name, the callback receives a generic `Event`.
   *
   * @param nameOrNames built-in or custom event name, or array of names
   * @param callback function that is executed when the event fires
   * @param element element to attach the listener to (defaults to `this`)
   *
   * @example
   * // Native event — callback typed as MouseEvent
   * this.listen('click', this.#handleClick);
   * @example
   * // Multiple events
   * this.listen(['click', 'keydown'], this.#handleEvent);
   * @example
   * // Custom event — callback typed as Event
   * this.listen('my-custom-event', this.#handleCustomEvent);
   * @example
   * // Different target element
   * this.listen('click', this.#handleClick, someOtherElement);
   */

  protected listen<K extends keyof GlobalEventHandlersEventMap>(
    nameOrNames: K | Array<K>,
    callback: (event: GlobalEventHandlersEventMap[K]) => void,
    element?: AllowedElements,
  ): void;

  protected listen(
    nameOrNames: string | Array<string>,
    callback: (event: Event) => void,
    element?: AllowedElements,
  ): void;

  protected listen<K extends keyof GlobalEventHandlersEventMap>(
    nameOrNames: K | Array<K> | string | Array<string>,
    callback: (event: GlobalEventHandlersEventMap[K] | Event) => void,
    element: AllowedElements = this,
  ) {
    const names = Array.isArray(nameOrNames) ? nameOrNames : [nameOrNames];
    names.forEach((name) => this.#addEventListener(name, callback, element));
  }

  override connectedCallback() {
    super.connectedCallback();
    this.onCreate?.();

    this.listen('focusin', (event) => {
      event.stopPropagation();

      document.dispatchEvent(
        new CustomEvent('customOnFocus', { detail: event.composedPath() }),
      );
    });
  }

  override disconnectedCallback() {
    super.disconnectedCallback();
    this.#removeEventListeners();
    this.onDestroy?.();
  }

  constructor() {
    super();

    onLanguageChanged(() => this.requestUpdate());
  }

  /**
   * This "Local" event dispatcher will stay in
   * the ShadowDOM because "composed" is false.
   */
  public dispatchLocalEvent({
    element = this,
    eventName,
    detail,
    bubbles = true,
    cancelable = false,
  }: DispatchCustomEvent) {
    return dispatchCustomEvent({
      element,
      eventName,
      detail,
      bubbles,
      cancelable,
      composed: false,
    });
  }

  /**
   * This "Global" event dispatcher will propagate
   * to the normal DOM because "composed" is true.
   */
  public dispatchGlobalEvent({
    element = this,
    eventName,
    detail,
    bubbles = true,
    cancelable = true,
  }: DispatchCustomEvent) {
    return dispatchCustomEvent({
      element,
      eventName,
      detail,
      bubbles,
      cancelable,
      composed: true,
    });
  }

  dispatchActivityEvent(type: UserActivityEventType, data: UserActivityEvent) {
    this.dispatchGlobalEvent({
      eventName: 'tmwc-activity',
      detail: {
        type,
        ...data,
      },
    });
  }

  public get slottedChildren() {
    const slot = this.shadowRoot?.querySelector('slot');
    return slot?.assignedElements({ flatten: true });
  }

  #addEventListener(
    name: string,
    callback: (event: Event) => void,
    element: AllowedElements = this,
  ) {
    element.addEventListener(
      name,
      callback as EventListenerOrEventListenerObject,
    );
    this.#listeners.push({
      name,
      callback: callback as EventListenerOrEventListenerObject,
      element,
    });
  }

  #removeEventListeners() {
    this.#listeners.forEach(({ name, element, callback }) =>
      element.removeEventListener(name, callback),
    );
  }
}
